import { Button } from "@svea/design-system-components-react";
import { ButtonGroup, Dialog, Grid, Heading, Stack, Text, useText } from "@sveabank/cc-common";
import Hide from "../../components/hide";
import Splash from "../../components/splash/splash";
import { usePageContext } from "../../hooks/context";
import { IconWithCircle } from "../new-company/components/icon-with-circle";
import { FreeCertificateText } from "./free-certificate-text";
import { useDashboard } from "./hooks";

type Props = {
	isOpen: boolean;
	closeModal: () => void;
};

export const NewClcApplicationModal = ({ isOpen, closeModal }: Props) => {
	const [t, texts] = useText();

	const { initClcApplication, isInitingClcApplication } = usePageContext(useDashboard);

	const onInitClick = async () => {
		await initClcApplication();
		closeModal();
	};

	return (
		<Dialog
			isOpen={isOpen}
			isDismissable={false}
			center
			size="auto"
			aria-label={t(texts.dashboardPage.newCompanyModal.modalHeading)}
		>
			<Dialog.Content>
				<Stack gap={8} align="start">
					<Stack direction="row" gap={6}>
						<Heading.H1 italic={false} size="h2">
							{t(texts.dashboardPage.newCompanyModal.modalHeading)}
						</Heading.H1>

						<Splash />
					</Stack>

					<Grid>
						<Grid.Col
							span={{
								base: 12,
								tablet: 8,
							}}
						>
							<Stack gap={4} align="start">
								<Heading.H2 italic={false} size="h3">
									{t(texts.dashboardPage.newCompanyModal.modalSubHeading)}
								</Heading.H2>

								<Stack gap={4} direction="row" justify="start" align="start">
									<Hide to="tablet">
										<IconWithCircle icon="INFO_DETAILS" />
									</Hide>

									<Stack>
										<Text align="start" bold className="text-text-headline">
											{t(texts.newCompany.processModalInfoHeading1)}
										</Text>
										<Text align="start">{t(texts.newCompany.processModalInfoText1)}</Text>
									</Stack>
								</Stack>
								<Stack gap={4} direction="row" justify="start" align="start">
									<Hide to="tablet">
										<IconWithCircle icon="ACCOUNTS" />
									</Hide>

									<Stack>
										<Text align="start" bold className="text-text-headline">
											{t(texts.newCompany.processModalInfoHeading2)}
										</Text>
										<FreeCertificateText />
									</Stack>
								</Stack>
								<Stack gap={4} direction="row" justify="start" align="start">
									<Hide to="tablet">
										<IconWithCircle icon="BANK" />
									</Hide>

									<Stack>
										<Text align="start" bold className="text-text-headline">
											{t(texts.newCompany.processModalInfoHeading3)}
										</Text>
										<Text align="start">{t(texts.newCompany.processModalInfoText3)}</Text>
									</Stack>
								</Stack>
								<Stack gap={4} direction="row" justify="start" align="start">
									<Hide to="tablet">
										<IconWithCircle icon="LIST_CHECK" />
									</Hide>

									<Stack>
										<Text align="start" bold className="text-text-headline">
											{t(texts.newCompany.processModalInfoHeading4)}
										</Text>
										<Text align="start">{t(texts.newCompany.processModalInfoText4)}</Text>
									</Stack>
								</Stack>
							</Stack>
						</Grid.Col>
					</Grid>
				</Stack>
			</Dialog.Content>
			<Dialog.Footer>
				<ButtonGroup row>
					<Button
						onPress={onInitClick}
						isPending={isInitingClcApplication}
						data-tracking-action="Initiate CLC Application"
					>
						{t(texts.dashboardPage.newCompanyModal.initButtonText)}
					</Button>

					<Button variant="secondary" onPress={() => closeModal()}>
						{t(texts.common.goBack)}
					</Button>
				</ButtonGroup>
			</Dialog.Footer>
		</Dialog>
	);
};
